import { useEffect, useRef } from 'react';
import { useProfileStore } from '@/stores/profileStore';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { handleAuthError } from '@/lib/authErrorHandler';
import type { Profile, ProfileUpdate } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Custom hook that provides profile management functionality
 * Wraps the Zustand profile store for easy consumption in React components
 */
export function useProfile() {
  const { user, isAuthenticated } = useAuth();
  const {
    profile,
    loading,
    error,
    fetchProfile,
    updateProfile,
    createProfile,
    clearProfile,
    setError,
    setProfile,
  } = useProfileStore();

  // Auto-fetch profile when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id && !profile) {
      fetchProfile(user.id);
    } else if (!isAuthenticated) {
      clearProfile();
    }
  }, [isAuthenticated, user?.id, profile, fetchProfile, clearProfile]);

  // Track subscription to prevent multiple subscriptions
  const subscriptionRef = useRef<RealtimeChannel | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3;

  // Subscribe to real-time profile updates
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      // Clean up subscription if user logs out
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      return;
    }

    // Don't create duplicate subscriptions
    if (subscriptionRef.current) return;

    // Reset reconnect attempts on new subscription
    reconnectAttemptsRef.current = 0;

    const channel = supabase
      .channel(`profile-changes-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          if (import.meta.env.DEV) {
            console.log('[useProfile] Real-time profile update:', payload.new);
          }
          if (payload.new) {
            // Update the profile in store
            setProfile(payload.new as Profile);
          }
        }
      );

    // Subscribe and handle connection errors
    channel.subscribe((status, error) => {
      if (status === 'SUBSCRIBED') {
        if (import.meta.env.DEV) {
          console.log('[useProfile] Realtime subscription active');
        }
        reconnectAttemptsRef.current = 0;
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn('[useProfile] Realtime subscription error:', status, error);
        reconnectAttemptsRef.current += 1;

        // If we've exceeded max reconnect attempts, this might be an auth issue
        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error(
            '[useProfile] Max reconnect attempts reached, possible auth issue'
          );
          // Clean up and don't retry
          if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
            subscriptionRef.current = null;
          }
          // Check if this is an auth error (session expired)
          handleAuthError();
        }
      } else if (status === 'CLOSED') {
        if (import.meta.env.DEV) {
          console.log('[useProfile] Realtime subscription closed');
        }
      }
    });

    subscriptionRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, setProfile]);

  const handleUpdateProfile = async (updates: ProfileUpdate) => {
    if (!user?.id) {
      setError('User not authenticated');
      return { error: 'User not authenticated' };
    }

    return updateProfile(user.id, updates);
  };

  const handleCreateProfile = async (profileData: Partial<Profile>) => {
    if (!user?.id) {
      setError('User not authenticated');
      return { error: 'User not authenticated' };
    }

    return createProfile(user.id, profileData);
  };

  return {
    // State
    profile,
    loading,
    error,
    isProfileLoaded: !!profile,
    
    // Actions
    fetchProfile: user?.id ? () => fetchProfile(user.id) : undefined,
    updateProfile: handleUpdateProfile,
    createProfile: handleCreateProfile,
    clearProfile,
    setError,
  };
}

/**
 * Hook to get current user's profile
 * Returns null if not authenticated or profile not loaded
 */
export function useUserProfile() {
  const { profile } = useProfile();
  return profile;
}

/**
 * Hook to check if user is on trial
 */
export function useIsTrialUser() {
  const { profile } = useProfile();
  return profile?.subscription_status === 'trial';
}

/**
 * Hook to get trial information
 * Returns trial info for both active trial users and free users (post-trial)
 */
export function useTrialInfo() {
  const { profile } = useProfile();
  
  if (!profile) {
    return null;
  }

  const isTrial = profile.subscription_status === 'trial';
  const isFree = profile.subscription_status === 'free';
  
  // Only return info for trial users or free users (who had a trial)
  if (!isTrial && !isFree) {
    return null;
  }

  const now = new Date();
  const trialEnd = profile.trial_end_date ? new Date(profile.trial_end_date) : null;
  const isExpired = trialEnd ? now > trialEnd : false;
  const daysRemaining = trialEnd ? Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : 0;

  return {
    isTrial,
    isFree,
    daysRemaining,
    isExpired,
    trialStartDate: profile.trial_start_date,
    trialEndDate: profile.trial_end_date,
  };
}
